import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { RappiderAlertModule, RappiderButtonModule, RappiderCheckboxModule, RappiderIconModule, RappiderLottiePlayerModule, RappiderParagraphModule, RappiderSpinModule } from '@rappider/rappider-components';
import { RappiderFormWithGuidanceModule } from 'libs/components/src/lib/components/form-with-guidance/form-with-guidance.module';
import { Subscription } from 'rxjs';
import { ConnectS3EditForm } from './utils/connect-s3-edit-form-config';
import { s3Content } from './utils/connect-s3-content';
import { CheckFileViewPermisson, ConnectFileExplorer, ConnectFileExplorerFailure, DeleteConnectedAccountByProvider, ResetDrive, UpdateConnectedAccountById } from '../../state/file-explorer.actions';
import { connectAlertConfig, connectIcon, errorAlertConfig } from './utils/file-list.configs';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { BrowserStorageManagementService } from 'libs/shared/src/lib/services/browser-storage-management-service/browser-storage-management.service';
import { environment } from '@environment';
import { ProvidersType } from 'libs/ai-integration/src/lib/utils/providers.enums';
import { FullPaths } from 'libs/shared/src/lib/definitions/page-definition';
import * as moment from 'moment-timezone';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'rappider-file-explorer-connect',
  standalone: true,
  imports: [
    CommonModule,
    RappiderSpinModule,
    RappiderLottiePlayerModule,
    RappiderFormWithGuidanceModule,
    NzDescriptionsModule,
    RappiderParagraphModule,
    NzBadgeModule,
    RappiderIconModule,
    RappiderButtonModule,
    RappiderAlertModule,
    RappiderCheckboxModule,
    FormsModule
  ],
  templateUrl: './connect.component.html',
  styleUrls: ['./connect.component.scss'],
})
export class RappiderFileExplorerConnectComponent implements OnInit, OnDestroy {

  subscriptions: Subscription[] = [];
  activeProvider: string | null = null;
  activeProviderByQueryParams: string | null = null;
  failedConnectText = '';
  successfullyConnectText = '';
  failedInformationText = 'User connection details could not be found. Please click the connect button.';
  successfullyInformationText = 'You can use the buttons below to access your file list and view your connection details.';
  editFormConfig = ConnectS3EditForm;
  content = s3Content;
  errorAlertConfig = errorAlertConfig;
  submitButtonLoading = false;
  loading = false;
  connectIcon = connectIcon;
  status = 'verified';
  connectedAccount: any;
  connectUrl: any;
  isAmazonS3FormVisible = false;
  errorMessage = '';
  connectAlertConfig = connectAlertConfig
  alertVisible = false;
  ProvidersType = ProvidersType;
  // Display config descriptions for drive
  descriptions;
  activePersonId: string;

  constructor(
    private router: Router,
    private store: Store<any>,
    private browserStorageManagementService: BrowserStorageManagementService,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    if (this.activatedRoute.snapshot.queryParams['provider']) {
      this.activeProviderByQueryParams = this.activatedRoute.snapshot.queryParams['provider'];
    }
    this.subscriptions = [
      this.subscribeToProviderWithConnectInformation(),
      this.subscribeToFileConnectSubmitButtonLoading(),
      this.subscribeToConnectFormError(),
      this.subscribeToConnectedAccount(),
      this.subscribeToConnectedAccountLoading(),
      this.subscribeToAlertVisibility(),
      this.subscribeToActiveProject(),
      this.subscribeToActivePersonId()
    ];
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.store.dispatch(ConnectFileExplorerFailure({ payload: { error: '', key: '', timestamp: 0 } }));
    this.store.dispatch(ResetDrive());
  }

  subscribeToProviderWithConnectInformation() {
    return this.store.select(state => state.fileExplorer?.activeProvider).subscribe((activeProvider) => {
      if (activeProvider) {
        this.activeProvider = activeProvider;
        this.connectUrl = `${environment.apiBaseUrl}/auth/drive/${activeProvider}/connect?authorizationToken=${this.browserStorageManagementService.getLocalStorageItem('Authorization')}`
        if (activeProvider === ProvidersType.AmazonS3) {
          this.failedConnectText = 'Amazon S3 Not Connected';
          this.successfullyConnectText = 'Successfully Connected to the Amazon S3';
        } else if (activeProvider === ProvidersType.Google) {
          this.failedConnectText = 'Google Drive Not Connected';
          this.successfullyConnectText = 'Successfully Connected to the Google Drive';
        } else if (activeProvider === ProvidersType.Microsoft) {
          this.failedConnectText = 'Microsoft OneDrive Not Connected';
          this.successfullyConnectText = 'Successfully Connected to the Microsoft OneDrive';
        } else {
          this.failedConnectText = ''
          this.successfullyConnectText = ''
        }
      } else if (!this.activeProviderByQueryParams) {
        this.router.navigate([FullPaths.AIDataSourcesCreate]);
      }
    });
  }

  subscribeToFileConnectSubmitButtonLoading() {
    return this.store.select(state => state.fileExplorer?.submitButtonLoading).subscribe((submitButtonLoading) => {
      this.submitButtonLoading = submitButtonLoading;
    });
  }

  subscribeToConnectedAccount() {
    return this.store.select(state => state.fileExplorer?.connectedAccount).subscribe((connectedAccount) => {
      this.connectedAccount = connectedAccount;
      if (connectedAccount?.provider === ProvidersType.Google) {
        this.descriptions = {
          'Active Provider': connectedAccount.provider,
          'Account Email': connectedAccount.accountEmail,
          'Account Name': connectedAccount.accountName,
          'Connected Date': moment(connectedAccount.createdDate).format('DD/MM/YYYY HH:mm')
        }
      }
      else if (connectedAccount?.provider === ProvidersType.Microsoft) {
        this.descriptions = {
          'Active Provider': connectedAccount.provider,
          'Account Email': connectedAccount.accountEmail,
          'Account Name': connectedAccount.accountName,
          'Connected Date': moment(connectedAccount.createdDate).format('DD/MM/YYYY HH:mm')
        }
      } else if (connectedAccount?.provider === ProvidersType.AmazonS3) {
        this.descriptions = {
          'Active Provider': connectedAccount.provider,
          'Account Name': connectedAccount.accountName,
        }
      }

      if (connectedAccount === null && this.activeProvider) {
        this.store.dispatch(ConnectFileExplorer({ payload: { provider: this.activeProvider } }));
      }
    });
  }

  subscribeToConnectedAccountLoading() {
    return this.store.select(state => state.fileExplorer?.connectedAccountLoading).subscribe((connectedAccountLoading) => {
      this.loading = connectedAccountLoading;
    });
  }

  subscribeToAlertVisibility() {
    return this.store.select(state => state.fileExplorer?.alertVisible).subscribe((alertVisible) => {
      this.alertVisible = alertVisible;
    });
  }

  subscribeToConnectFormError() {
    return this.store.select(state => state.fileExplorer?.error).subscribe((error) => {
      if (error?.error?.error) {
        this.errorAlertConfig.description.text = error?.error?.error?.message || error?.error?.error;
      } else {
        this.errorAlertConfig.description.text = null;
      }
    });
  }

  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject.data).subscribe(activeProject => {
      if (activeProject && this.activeProviderByQueryParams && !this.activeProvider) {
        this.store.dispatch(ConnectFileExplorer({ payload: { provider: this.activeProviderByQueryParams } }))
      }
    });
  }

  subscribeToActivePersonId() {
    return this.store.select(state => state.auth?.activePerson?.id).subscribe(activePersonId => {
      this.activePersonId = activePersonId;
    });
  }

  onFormSubmit(form) {
    if (this.activeProvider) {
      this.store.dispatch(ConnectFileExplorer({ payload: { provider: this.activeProvider, accessKey: form.accessKey, secretAccessKey: form.secretAccessKey, region: form.region, bucketName: form.bucketName } }))
      this.isAmazonS3FormVisible = false;
    }
  }

  goToFileListButtonClick() {
    this.store.dispatch(CheckFileViewPermisson());
  }

  refreshConnectButtonClick() {
    this.isAmazonS3FormVisible = true;
  }

  disconnect() {
    if (this.activeProvider) {
      this.store.dispatch(DeleteConnectedAccountByProvider({ payload: { provider: this.activeProvider } }));
    }
  }

  onIsFileAccessSharedChange(isFileAccessShared: boolean) {
    this.store.dispatch(UpdateConnectedAccountById({ payload: { id: this.connectedAccount?.id, isFileAccessShared } }));
  }
}

